// app/javascript/controllers/api_tester_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    endpoint: String,
    title: String,
    authEnabled: Boolean,
    demoToken: String,
    environment: String
  }
  
  connect() {
    // Set default values if not provided
    if (!this.hasAuthEnabledValue) {
      this.authEnabledValue = false;
    }
    if (!this.hasEnvironmentValue) {
      this.environmentValue = 'development';
    }
  }
  
  // Main method for handling API test clicks
  test(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const endpoint = this.endpointValue || button.dataset.endpoint;
    const title = this.titleValue || button.dataset.title || 'API Response';
    
    this.makeApiCall(endpoint, title, button);
  }
  
  // Method for testing custom endpoints (from input field)
  testCustom(event) {
    event.preventDefault();
    
    const input = this.element.querySelector('#custom-api-endpoint');
    if (!input) return;
    
    const endpoint = input.value.trim();
    
    if (!endpoint) {
      alert('Please enter an API endpoint');
      return;
    }
    
    // Normalize the endpoint
    let normalizedEndpoint = endpoint;
    if (!normalizedEndpoint.startsWith('/')) {
      normalizedEndpoint = '/api/' + normalizedEndpoint;
    } else if (!normalizedEndpoint.startsWith('/api/')) {
      normalizedEndpoint = '/api' + normalizedEndpoint;
    }
    
    const button = event.currentTarget;
    this.makeApiCall(normalizedEndpoint, 'Custom API Test', button);
  }
  
  // Handle Enter key in custom endpoint input
  handleEnterKey(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const button = this.element.querySelector('[data-action*="testCustom"]');
      if (button) {
        button.click();
      }
    }
  }
  
  makeApiCall(endpoint, title, button) {
    // Store original button state
    const originalHTML = button.innerHTML;
    button.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span> Loading...';
    button.disabled = true;
    
    const headers = {
      'Accept': 'application/json'
    };
    
    // Add auth header if enabled
    if (this.authEnabledValue && this.demoTokenValue) {
      headers['X-Factory-Token'] = this.demoTokenValue;
    }
    
    fetch(endpoint, { headers: headers })
      .then(response => {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return response.json().then(data => ({
            data: data,
            status: response.status,
            statusText: response.statusText,
            ok: response.ok,
            contentType: contentType
          }));
        } else {
          return response.text().then(text => ({
            data: { error: 'Response was not JSON', text: text },
            status: response.status,
            statusText: response.statusText,
            ok: false,
            contentType: contentType
          }));
        }
      })
      .then(result => {
        this.openResultWindow(endpoint, title, result);
      })
      .catch(error => {
        console.error('API Request failed:', error);
        alert('API Request Failed: ' + error.message);
      })
      .finally(() => {
        button.innerHTML = originalHTML;
        button.disabled = false;
      });
  }
  
  openResultWindow(endpoint, title, result) {
    const newWindow = window.open('', '_blank');
    
    if (!newWindow) {
      alert('Please allow popups for this site to view API responses in new tabs.');
      return;
    }
    
    // Focus the window (for Safari)
    newWindow.focus();
    
    const doc = newWindow.document;
    doc.open();
    
    // Write the HTML document
    doc.write('<!DOCTYPE html>');
    doc.write('<html><head>');
    doc.write('<meta charset="UTF-8">');
    doc.write('<meta name="viewport" content="width=device-width, initial-scale=1.0">');
    doc.write('<title>' + this.escapeHtml(title) + ' - Factory API</title>');
    doc.write('<link href="https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css" rel="stylesheet">');
    doc.write('<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.8.0/font/bootstrap-icons.css">');
    doc.write(this.getStyles());
    doc.write('</head><body>');
    
    // Header
    doc.write('<div class="header"><div class="container">');
    doc.write('<div class="d-flex justify-content-between align-items-start">');
    doc.write('<div>');
    doc.write('<h3>' + this.escapeHtml(title) + '</h3>');
    doc.write('<div class="endpoint">' + this.escapeHtml(endpoint) + '</div>');
    doc.write('</div>');
    doc.write('<div class="status-badge ' + (result.ok ? 'success' : 'danger') + '">');
    doc.write((result.ok ? '✓ ' : '✗ ') + result.status + ' ' + result.statusText);
    doc.write('</div>');
    doc.write('</div>');
    doc.write('</div></div>');
    
    // Body with JSON output
    doc.write('<div class="container">');
    doc.write('<div class="json-container">');
    doc.write('<button class="btn btn-sm btn-outline-secondary copy-btn" id="copyBtn">');
    doc.write('<i class="bi bi-clipboard"></i> Copy JSON');
    doc.write('</button>');
    doc.write('<pre id="json-output">' + JSON.stringify(result.data, null, 2) + '</pre>');
    doc.write('</div>');
    
    // Metadata
    doc.write('<div class="metadata">');
    doc.write('<h6>Response Metadata</h6>');
    doc.write('<div class="row">');
    doc.write('<div class="col-md-6">');
    doc.write('<small class="text-muted">');
    doc.write('<strong>Timestamp:</strong> ' + new Date().toLocaleString() + '<br>');
    doc.write('<strong>Status Code:</strong> ' + result.status + '<br>');
    doc.write('<strong>Content Type:</strong> ' + (result.contentType || 'Not specified'));
    doc.write('</small>');
    doc.write('</div>');
    doc.write('<div class="col-md-6">');
    doc.write('<small class="text-muted">');
    doc.write('<strong>Authentication:</strong> ' + (this.authEnabledValue ? 'Enabled (using demo token)' : 'Disabled') + '<br>');
    doc.write('<strong>Method:</strong> GET<br>');
    doc.write('<strong>Environment:</strong> ' + this.environmentValue);
    doc.write('</small>');
    doc.write('</div>');
    doc.write('</div>');
    doc.write('</div>');
    
    doc.write('</div>');
    
    // Add copy functionality
    doc.write('<script>');
    doc.write('document.getElementById("copyBtn").addEventListener("click", function() {');
    doc.write('  const jsonText = document.getElementById("json-output").textContent;');
    doc.write('  navigator.clipboard.writeText(jsonText).then(() => {');
    doc.write('    this.innerHTML = \'<i class="bi bi-check"></i> Copied!\';');
    doc.write('    setTimeout(() => {');
    doc.write('      this.innerHTML = \'<i class="bi bi-clipboard"></i> Copy JSON\';');
    doc.write('    }, 2000);');
    doc.write('  });');
    doc.write('});');
    doc.write('</script>');
    
    doc.write('</body></html>');
    doc.close();
    
    // Focus again after content loads (for Safari)
    newWindow.focus();
  }
  
  getStyles() {
    return `<style>
      pre { 
        background: #f8f9fa; 
        padding: 1rem; 
        border-radius: 0.375rem; 
        overflow-x: auto; 
        font-size: 0.875rem; 
        line-height: 1.5; 
        max-height: 70vh;
      }
      .header { 
        background: linear-gradient(135deg, #198754 0%, #157347 100%); 
        color: white; 
        padding: 1.5rem 0; 
        margin-bottom: 1.5rem; 
        box-shadow: 0 2px 4px rgba(0,0,0,0.1); 
      }
      .endpoint { 
        font-family: 'Courier New', monospace; 
        background: rgba(255,255,255,0.15); 
        padding: 0.5rem 0.75rem; 
        border-radius: 0.25rem; 
        margin-top: 0.5rem; 
        word-break: break-all; 
      }
      .status-badge { 
        display: inline-block; 
        padding: 0.25rem 0.75rem; 
        border-radius: 1rem; 
        font-size: 0.875rem; 
        font-weight: 500; 
      }
      .status-badge.success { 
        background: rgba(255,255,255,0.9); 
        color: #198754; 
      }
      .status-badge.danger { 
        background: rgba(255,255,255,0.9); 
        color: #dc3545; 
      }
      .json-container { 
        position: relative; 
      }
      .copy-btn { 
        position: absolute; 
        top: 0.5rem; 
        right: 0.5rem; 
        z-index: 10; 
      }
      .metadata { 
        background: #f8f9fa; 
        border-left: 3px solid #198754; 
        padding: 1rem; 
        margin-top: 2rem; 
        border-radius: 0.25rem; 
      }
      .metadata h6 {
        color: #495057;
        margin-bottom: 1rem;
      }
    </style>`;
  }
  
  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return String(text).replace(/[&<>"']/g, m => map[m]);
  }
}
